import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { Expense } from '#/graphql/types';
import type { FormAction, FormItem, FormRefType } from '#/common/components/Form';
import Form from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import AttachmentsUpload from '#/common/components/AttachmentUpload';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { get } from 'lodash';
import ScanSelector from '#/common/components/ScanSelector';
import useOcrProcessing, { OcrFieldResult } from '#/common/hooks/useOcrProcessing';
import useUploadScannedFile from '#/common/hooks/useUploadScannedFile';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import { setEditorOptions } from '#/common/components/Form/formItemOption';
import { extractFieldNames } from '#/common/utils/extractFieldNames';

// OCR field constants for expense receipts
const OCR_COMPANY_NAME = 'company name';
const OCR_DATE = 'date';
const OCR_TOTAL_AMOUNT = 'total amount';
const OCR_DESCRIPTION = 'description';

export interface ExpenseFormProps {
  expense: Expense;
  title: string;
  fields: FormItem<Expense>[];
  submit?: (
    expense: Expense,
  ) => Promise<{ data?: Expense; errors?: string[] } | undefined> | undefined;
  deleteExpense?: () => unknown;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  expense,
  title,
  fields,
  submit,
  deleteExpense,
}) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);
  const uploadScannedFile = useUploadScannedFile();
  const { getOcrSuccessMessage } = useOcrProcessing();

  // State for ScanSelector
  const id = get(expense, 'id', undefined);
  const [showExpenseScanSelector, setShowExpenseScanSelector] = useState(!id); // Show for new expenses
  const [tenantId, setTenantId] = useState<string | undefined>(get(expense, 'tenantId'));
  const [scannedFile, setScannedFile] = useState<File | null>(null);

  const statusIsExpended = expense.status === 'expended';

  const actions = useMemo(() => {
    const result: FormAction<Expense>[] = [];

    if (deleteExpense) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        disabled: statusIsExpended,
        onClick: async (e) => {
          const sure = await confirm(
            'error',
            `Are you sure you want to delete the expense: ${e.record?.title}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteExpense();
          e.setIsLoading(false);

          navigate('/expenses');
        },
      });
    }

    if (submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        disabled: statusIsExpended,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          // Check if status is being changed to "Expended"
          const isChangingToExpended =
            expense.status !== 'expended' && e.record.status === 'expended';

          if (isChangingToExpended) {
            // Show warning modal
            const proceed = await confirm(
              'warning',
              "Warning: Marking this expense as 'Expended' will finalize it and add its cost to the referenced Work Order. You will no longer be able to edit or delete this expense. Do you want to proceed?",
            );

            if (!proceed) {
              return;
            }
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\n`));
              return;
            }
            if (!id && result && result.data?.id) {
              // Upload scanned file if available
              if (scannedFile) {
                await uploadScannedFile('expenses', result.data.id, scannedFile);
                setScannedFile(null); // Clear after successful upload
              }

              navigate(`/expenses/${result.data.id}`, { replace: true });
              return;
            }

            // If status changed to expended, make all fields read-only
            if (isChangingToExpended && e.form) {
              const allFieldNames = extractFieldNames(fields);
              allFieldNames.forEach((fieldName) => {
                setEditorOptions(e.form!, fieldName, { readOnly: true });
              });
            }

            void alert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (e: any) {
            console.log('Error', e);
            void alert('error', 'Missing or invalid data!');
          } finally {
            e.setIsLoading(false);
          }
        },
      });
    }

    return result;
  }, [alert, confirm, deleteExpense, navigate, submit, expense, scannedFile, statusIsExpended]);

  // Handle expense scan selection
  const fieldsToScan = [OCR_COMPANY_NAME, OCR_DATE, OCR_TOTAL_AMOUNT, OCR_DESCRIPTION];
  const handleExpenseScanSelect = useCallback(
    (file: File | null, result: OcrFieldResult, error?: string) => {
      if (!formRef.current) return;

      if (error) {
        console.error('Error processing image:', error);
        void alert('error', `Error processing image: ${error}`);
        return;
      }

      if (file) {
        setScannedFile(file);
      }

      const populatedFields: string[] = [];

      if (result[OCR_COMPANY_NAME]) {
        formRef.current?.updateData(
          'title',
          `${result[OCR_COMPANY_NAME]} ${result[OCR_DATE] || ''}`.trim(),
        );
        populatedFields.push(allFields.title.label);
      }
      if (result[OCR_TOTAL_AMOUNT]) {
        // Extract numeric value from amount (e.g., "$38.37" -> 38.37)
        const amount = parseFloat(result[OCR_TOTAL_AMOUNT].replace(/[^0-9.]/g, ''));
        formRef.current?.updateData('amount', amount);
        populatedFields.push(allFields.amount.label);
      }
      if (result[OCR_DESCRIPTION]) {
        formRef.current?.updateData('description', result[OCR_DESCRIPTION]);
        populatedFields.push(allFields.description.label);
      }

      // Repaint the form to show the updated values
      formRef.current?.repaint();
      setShowExpenseScanSelector(false);

      // Show success alert with populated fields
      const successMessage = getOcrSuccessMessage(populatedFields);
      if (successMessage) {
        void alert('success', successMessage);
      }
    },
    [],
  );

  const onCloseExpenseScanSelector = useCallback(() => {
    setShowExpenseScanSelector(false);
  }, []);

  const onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    const { component: form, dataField: field } = e;
    const formData = form.option('formData');
    if (field === 'tenantId') {
      setTenantId(formData.tenantId);
    }
  }, []);

  return (
    <>
      {/* Show ScanSelector for new expenses after tenant is selected */}
      {showExpenseScanSelector && tenantId && (
        <ScanSelector
          onOcrResult={handleExpenseScanSelect}
          onClose={onCloseExpenseScanSelector}
          modalChoiceText="Scan Receipt"
          fieldsToScan={fieldsToScan}
        />
      )}

      <Form
        ref={formRef}
        readonly={statusIsExpended}
        fields={fields}
        record={expense}
        actions={actions}
        supportTenant
        onFieldDataChanged={onFieldDataChanged}
      />
      {expense.id && (
        <AttachmentsUpload
          relatedTable="expenses"
          relatedItem={expense.id}
          disableUpload={statusIsExpended}
        />
      )}
    </>
  );
};

export default React.memo(ExpenseForm);
